import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Banner, Button, Field, Input } from '../components/ui';
import { api } from '../lib/api';
import { BETA_AGREEMENT_BODY, BETA_AGREEMENT_VERSION, isScrolledToEnd } from '../lib/betaAgreement';
import { useSession } from '../lib/session';

/**
 * The Early Access Agreement wall. Until this is accepted the backend 403s
 * every authenticated route, so this page is the only way into the product.
 *
 * Accept stays disabled until the reader reaches the bottom of the text: this
 * is a sworn-ish commercial agreement, and a one-click accept on unread terms
 * is worth less than the ink. The agreement body itself lives in
 * lib/betaAgreement.ts so replacing the words touches nothing here.
 */
export default function BetaAgreement() {
  const { contractor, refresh } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get('returnTo') || '/';

  const paneRef = useRef<HTMLDivElement>(null);
  const [readToEnd, setReadToEnd] = useState(false);
  const [signerName, setSignerName] = useState(contractor?.personVerifiedName ?? '');
  const [companyName, setCompanyName] = useState(contractor?.companyName ?? '');
  const [title, setTitle] = useState('');
  const [licenseNumber, setLicenseNumber] = useState(contractor?.licenseNumber ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkScroll = useCallback(() => {
    const el = paneRef.current;
    if (el && isScrolledToEnd(el)) setReadToEnd(true);
  }, []);

  // An agreement short enough not to scroll is already read.
  useEffect(() => checkScroll(), [checkScroll]);

  const complete = signerName.trim() && companyName.trim() && title.trim() && licenseNumber.trim();

  async function accept() {
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/api/me/beta-agreement', {
        version: BETA_AGREEMENT_VERSION,
        signerName: signerName.trim(),
        companyName: companyName.trim(),
        title: title.trim(),
        licenseNumber: licenseNumber.trim(),
      });
      // Re-read /api/me so the guard sees the acceptance before we navigate.
      await refresh();
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record your acceptance');
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <p className="text-2xl font-extrabold tracking-tight text-ink">
        Lien<span className="text-tint">Guardian</span>
      </p>
      <h1 className="mt-6 text-xl font-bold text-ink">Early Access Agreement</h1>
      <p className="mt-2 text-sm text-muted">
        Version {BETA_AGREEMENT_VERSION}. Read it through, then sign below. Nothing else in the
        product opens until you do.
      </p>

      <div
        ref={paneRef}
        onScroll={checkScroll}
        data-testid="agreement-body"
        className="mt-6 h-96 overflow-y-auto rounded border border-border bg-surface p-5 text-sm leading-relaxed text-muted"
      >
        {BETA_AGREEMENT_BODY.map((section) => (
          <section key={section.heading} className="mb-6 last:mb-0">
            <h2 className="mb-2 font-semibold text-ink">{section.heading}</h2>
            {section.body.map((block, i) =>
              typeof block === 'string' ? (
                <p key={i} className="mb-2 last:mb-0">
                  {block}
                </p>
              ) : (
                <ul key={i} className="mb-2 list-disc space-y-1 pl-5 last:mb-0">
                  {block.bullets.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              ),
            )}
          </section>
        ))}
      </div>

      {!readToEnd && (
        <p className="mt-2 text-xs text-dim">Scroll to the end of the agreement to enable Accept.</p>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Field label="Your name">
          <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Jean Coastal" />
        </Field>
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Owner" />
        </Field>
        <Field label="Company name">
          <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Coastal Drywall LLC" />
        </Field>
        <Field label="FL license number">
          <Input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} placeholder="CBC1250011" />
        </Field>
      </div>

      {error && (
        <div className="mt-4">
          <Banner title="Could not record your acceptance">{error}</Banner>
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <Button onClick={accept} disabled={!readToEnd || !complete || submitting}>
          {submitting ? 'Recording…' : 'I Accept'}
        </Button>
        {!complete && readToEnd && <span className="text-xs text-dim">Fill in all four fields.</span>}
      </div>
    </div>
  );
}
